/**
 * B830 — the recall-at-boot facts belong to ONE tenant. Session storage is
 * per ORIGIN, and the shared `store.dev` host serves every tenant by
 * `?tenant=`: dev-ferry → dev-zoo in one tab recalled the ferry's name, theme
 * and publishable key on the zoo until the mint answered, and kept them where
 * the zoo has none. So the page's tenant is remembered beside the facts, and
 * at boot — before the first render reads any of them — a stranger's facts
 * are forgotten whole. The publishable key is the fact that must never cross;
 * the bearer and the cart go with it (another tenant's cart is not this one's).
 *
 * A custom-domain tenant never meets this (its origin is its own). B831 — a
 * site-key boot does not know its tenant until the resolve answers, but it
 * knows its site key: that is its identity here (`site:<key>`), so another
 * tenant's or site's facts are forgotten before the first render too. A
 * tenant reached by both `?tenant=` and `?siteKey=` in one tab pays one
 * re-mint for it — the conservative direction, the key never crosses.
 */
import type { StorageLike } from "./session.js";

export const TENANT_KEY = "transact.storefront.tenant";

/** Every key theme.ts and session.ts persist — walled against drift in b830HeadAndTenant.test.ts. */
export const TENANT_FACT_KEYS: readonly string[] = [
  "transact.storefront.token",
  "transact.storefront.tokenExpiresAt",
  "transact.storefront.cartId",
  "transact.storefront.channelTheme",
  "transact.storefront.publishableKey",
  "transact.storefront.stripeAccount",
  "transact.storefront.businessName",
  "transact.storefront.venue",
  "transact.storefront.tenantType",
  "transact.storefront.clock",
  "transact.storefront.gtmContainerId",
  "transact.storefront.groupThreshold",
  "transact.storefront.groupsContactName",
  "transact.storefront.memberships",
];

export function keepTenantFacts(storage: StorageLike | null, tenantId: string | undefined, siteKey?: string): void {
  // a known tenant outranks a site key; a page with neither decides nothing; a known one keeps its own facts or none
  const identity = tenantId !== undefined && tenantId !== "" ? tenantId : siteKey !== undefined && siteKey !== "" ? `site:${siteKey}` : undefined;
  if (storage !== null && identity !== undefined) forgetStrangers(storage, identity);
}

function forgetStrangers(storage: StorageLike, identity: string): void {
  try {
    if (storage.getItem(TENANT_KEY) !== identity) {
      for (const key of TENANT_FACT_KEYS) storage.removeItem(key);
      storage.setItem(TENANT_KEY, identity);
    }
  } catch {
    /* memory-only sessions just re-mint */
  }
}
